const initFitFinder = (section) => {
  const tabs = [...section.querySelectorAll('[data-otk-product-fit-tab]')];
  const panels = [...section.querySelectorAll('[data-otk-product-fit-panel]')];
  if (!tabs.length || !panels.length) return;

  const select = (id) => {
    tabs.forEach((tab) => {
      const selected = tab.dataset.otkProductFitTab === id;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    panels.forEach((panel) => { panel.hidden = panel.dataset.otkProductFitPanel !== id; });
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => select(tab.dataset.otkProductFitTab));
    tab.addEventListener('keydown', (event) => {
      if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let next = index;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (['ArrowDown', 'ArrowRight'].includes(event.key)) next = (index + 1) % tabs.length;
      if (['ArrowUp', 'ArrowLeft'].includes(event.key)) next = (index - 1 + tabs.length) % tabs.length;
      tabs[next].focus();
      select(tabs[next].dataset.otkProductFitTab);
    });
  });
};

const initProductPointLinks = (pointList) => {
  if (pointList.dataset.otkProductPointsInitialized === 'true') return;

  const buttons = [...pointList.querySelectorAll('[data-otk-product-point]')];
  const productSection = pointList.closest('.shopify-section');
  if (!buttons.length || !productSection) return;

  let slideshow;
  let galleryObserver;

  const sync = (index = slideshow?.current) => {
    const activePosition = Number(index) || 0;
    buttons.forEach((button) => {
      const selected = Number(button.dataset.otkProductPoint) === activePosition;
      button.setAttribute('aria-pressed', String(selected));
    });
  };

  const bindGallery = () => {
    const nextSlideshow = productSection.querySelector('.product-information__media slideshow-component');
    if (!nextSlideshow || nextSlideshow === slideshow) return;

    if (slideshow) slideshow.removeEventListener('slideshow:select', handleSelect);
    slideshow = nextSlideshow;
    slideshow.addEventListener('slideshow:select', handleSelect);
    sync();
  };

  const handleSelect = (event) => sync(event.detail?.index);

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      bindGallery();
      const position = Number(button.dataset.otkProductPoint);
      if (!slideshow || !Number.isInteger(position)) return;
      slideshow.select(position);
    });
  });

  bindGallery();
  galleryObserver = new MutationObserver(bindGallery);
  galleryObserver.observe(productSection, { childList: true, subtree: true });
  pointList.dataset.otkProductPointsInitialized = 'true';
};

const initSelectedVariantLine = (line) => {
  if (line.dataset.otkSelectedVariantInitialized === 'true') return;

  const productSection = line.closest('.shopify-section');
  if (!productSection) return;

  const update = (title) => {
    if (!title) return;
    line.textContent = `${title} selected`;
  };

  const selectedPickerTitle = () => {
    const selectedOption = productSection.querySelector('variant-picker input:checked, variant-picker select option:checked');
    return selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement
      ? selectedOption.value
      : '';
  };

  productSection.addEventListener('shopify:product:select', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;

    event.promise
      ?.then(({ detail }) => update(detail?.resource?.title || selectedPickerTitle()))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[otk-product] Selected variant update failed:', error);
      });
  });

  line.dataset.otkSelectedVariantInitialized = 'true';
};

const initProductCarousel = (carousel) => {
  if (carousel.dataset.otkProductCarouselInitialized === 'true') return;

  const track = carousel.querySelector('[data-otk-product-carousel]');
  const controls = carousel.querySelector('[data-otk-product-carousel-controls]');
  const previousButton = carousel.querySelector('[data-carousel-prev]');
  const nextButton = carousel.querySelector('[data-carousel-next]');
  if (!track) return;

  const updateControls = () => {
    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const hasOverflow = maximumScroll > 2;

    if (controls) controls.hidden = !hasOverflow;
    if (previousButton) previousButton.disabled = !hasOverflow || track.scrollLeft <= 2;
    if (nextButton) nextButton.disabled = !hasOverflow || track.scrollLeft >= maximumScroll - 2;
  };

  const scroll = (direction) => {
    const card = track.querySelector('[data-otk-product-carousel-card]');
    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const cardWidth = card?.getBoundingClientRect().width || track.clientWidth;
    const visibleCards = Math.max(1, Math.floor((track.clientWidth + gap) / (cardWidth + gap)));
    const distance = (cardWidth + gap) * visibleCards;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({ left: direction * distance, behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  previousButton?.addEventListener('click', () => scroll(-1));
  nextButton?.addEventListener('click', () => scroll(1));
  track.addEventListener('scroll', () => window.requestAnimationFrame(updateControls), { passive: true });

  if ('ResizeObserver' in window) {
    new ResizeObserver(updateControls).observe(track);
  }

  window.requestAnimationFrame(updateControls);
  carousel.dataset.otkProductCarouselInitialized = 'true';
};

const initProductRecommendations = (section) => {
  if (section.dataset.otkProductRecommendationsInitialized === 'true') return;

  initProductCarousel(section);
  section.dataset.otkProductRecommendationsInitialized = 'true';

  if (
    section.dataset.source !== 'automatic' ||
    section.dataset.recommendationsPerformed === 'true' ||
    !section.dataset.url
  ) {
    return;
  }

  const loadRecommendations = async () => {
    if (section.dataset.otkProductRecommendationsLoading === 'true') return;
    section.dataset.otkProductRecommendationsLoading = 'true';

    try {
      const response = await fetch(section.dataset.url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Recommendations request returned ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = documentFragment.querySelector('[data-otk-product-recommendations]');
      if (!replacement) throw new Error('Recommendations response did not include the OTK section');

      const responseHasProducts = replacement.dataset.hasProducts === 'true';
      if (!responseHasProducts && section.dataset.designMode === 'true') {
        section.dataset.recommendationsPerformed = 'true';
        return;
      }

      if (!responseHasProducts) {
        section.hidden = true;
        return;
      }

      section.replaceWith(replacement);
      initProductRecommendations(replacement);
    } catch (error) {
      if (section.dataset.designMode !== 'true') section.hidden = true;
      console.warn('[otk-product] Product recommendations could not be loaded:', error);
    }
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      loadRecommendations();
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(section);
  } else {
    loadRecommendations();
  }
};

const initializeOtkProducts = (scope = document) => {
  scope.querySelectorAll('[data-otk-product-fit]').forEach(initFitFinder);
  scope.querySelectorAll('[data-otk-product-points]').forEach(initProductPointLinks);
  scope.querySelectorAll('[data-otk-product-selected-variant]').forEach(initSelectedVariantLine);
  scope.querySelectorAll('[data-otk-product-recommendations]').forEach(initProductRecommendations);
};

document.addEventListener('DOMContentLoaded', () => initializeOtkProducts());
document.addEventListener('shopify:section:load', (event) => initializeOtkProducts(event.target));
